import { Autocomplete, Card, Grid, Icon, IconButton, InputLabel, Tooltip } from "@mui/material";
import MDInput from "components/MDInput";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import DataTable from "examples/Tables/DataTable";
import DefaultCell from "layouts/dashboards/sales/components/DefaultCell";
import { useCallback, useEffect, useRef, useState } from "react";
import { verifyVariable } from "utils/Functions";
import { post } from "utils/WebService";
import TipoOcorrenciaCreateEdit from "./TipoOcorrenciaCreateEdit";
import { categoriaOcorrenciaEnum } from "utils/Enums";
import { useUser } from "context/UserContext";

export default function TipoOcorrenciaConsult() {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const { userPerfils } = useUser();
    const isAdminOrSuporte = userPerfils && userPerfils.some(p => {
        const pName = (p?.nome || (typeof p === 'string' ? p : "")).toUpperCase();
        return p?.tipoPerfil === 4 || p?.tipoPerfil === 6 || pName === 'ADMINISTRADOR' || pName === 'SUPORTE';
    });

    const tableState = useRef({
        pageSize: 10,
        pageIndex: 0,
        sorting: [],
        filtered: [],
    });

    const [data, setData] = useState([]);
    const [pages, setPages] = useState(0);
    const [loadingTable, setLoadingTable] = useState(true);
    const [isCreateEdit, setIsCreateEdit] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [recordsTotal, setRecordsTotal] = useState(0);
    const [aplicar, setAplicar] = useState(false);

    const [filters, setFilters] = useState({
        nome: '',
        ativo: '',
        categoria: ''
    });

    useEffect(() => {
        fetchData({ ...tableState });
    }, []);

    const fetchData = useCallback(({ pageSize, pageIndex, sorting, filtered }) => {
        setLoadingTable(true);
        const updatedTableState = {
            pageSize: pageSize ? pageSize : tableState.current.pageSize,
            pageIndex: (pageIndex == 0 || pageIndex) ? pageIndex : tableState.current.pageIndex,
            sorting: sorting ? sorting : tableState.current.sorting,
            filtered: filtered ? filtered : tableState.current.filtered,
        };
        tableState.current = updatedTableState;

        post("/tipo-ocorrencia/list", {
            page: updatedTableState.pageIndex,
            pageSize: updatedTableState.pageSize,
            sorted: updatedTableState.sorting,
            filtered: updatedTableState.filtered,
        }).then(({ data, pages, recordsTotal }) => {
            setPages(pages);
            setData(data || []);
            setRecordsTotal(recordsTotal);
            setLoadingTable(false);
            setAplicar(false);
        }).catch(() => {
            setLoadingTable(false)
            setAplicar(false);
        });
    }, []);

    const backFunction = () => {
        fetchData({ ...tableState });
        setIsCreateEdit(false);
        setSelectedId(null);
    }

    const userColumns = [
        {
            header: "nome",
            accessorKey: "nome",
            cell: info => <DefaultCell>{info.getValue() || ""}</DefaultCell>,
            isSorted: true
        },
        {
            header: "categoria",
            accessorKey: "categoria",
            cell: info => {
                const val = info.getValue();
                const cat = categoriaOcorrenciaEnum.find(x => x.code === val);
                return <DefaultCell>{cat ? cat.label : "Ocorrência"}</DefaultCell>;
            },
            isSorted: true
        },
        {
            header: "Situação da Ficha",
            accessorKey: "situacaoFichaLabel",
            cell: info => {
                const label = info.getValue() || info.row.original.situacaoFichaLabel;
                if (label) return <DefaultCell>{label}</DefaultCell>;

                const code = info.row.original.situacaoFicha;
                const situacaoOpt = [
                    { code: 1, label: "Devolvido" },
                    { code: 2, label: "Cobrança" },
                    { code: 3, label: "Negociação" },
                    { code: 4, label: "Pesquisa" },
                    { code: 5, label: "Suspenso" },
                    { code: 6, label: "Migrado" },
                    { code: 7, label: "Quitado" }
                ].find(s => s.code === code);

                return <DefaultCell>{situacaoOpt ? situacaoOpt.label : "-"}</DefaultCell>;
            },
            isSorted: true
        },
        {
            header: "ativo",
            accessorKey: "ativo",
            cell: info => <DefaultCell>{info.getValue() ? "Sim" : "Não"}</DefaultCell>,
            isSorted: false
        },
        {
            header: "ações",
            isSorted: false,
            cell: info => (
                <Tooltip title="Editar">
                    <IconButton
                        onClick={() => {
                            const rowId = info.row.original.id || info.row.original.cvId;
                            if (rowId) {
                                setSelectedId(rowId);
                                setIsCreateEdit(true);
                            } else {
                                console.error("ID não encontrado no objeto:", info.row.original);
                                alert("Erro: O ID da ocorrência não foi retornado pelo servidor. Por favor, recarregue a página ou verifique o backend.");
                            }
                        }}>
                        <MDTypography variant="h4">
                            <Icon color="inherit" sx={{ marginRight: 1 }}>edit</Icon>
                        </MDTypography>
                    </IconButton>
                </Tooltip>
            ),
            width: "15%"
        },
    ];

    const handleApplyFilter = () => {
        let filtered = [];
        Object.keys(filters).forEach(filterKey => {
            if (filters[filterKey] !== '' && filters[filterKey] !== null && filters[filterKey] !== undefined) {
                filtered.push({ id: filterKey, value: filters[filterKey] });
            }
        })
        setAplicar(true);
        fetchData({ ...tableState, pageIndex: 0, filtered });
    };

    const handleCleanFilter = () => {
        setFilters({ nome: '', ativo: '', categoria: '' })
        fetchData({ pageSize: 10, pageIndex: 0, sorting: [], filtered: [] });
    };

    return (
        <Grid container display="flex" justifyContent="center">
            {isCreateEdit &&
                <Grid item xs={12} md={12}>
                    <TipoOcorrenciaCreateEdit id={selectedId} closeCreateEdit={backFunction} />
                </Grid>
            }
            <Grid item xs={12} md={12} sx={{ display: isCreateEdit ? "none" : "" }}>
                <Card variant="outlined" sx={{ px: 2, py: 1.5, mb: 2, background: darkMode ? "#4B4F5C" : "#fafafa", borderColor: darkMode ? "#333" : "#ddd" }}>
                    <Grid container spacing={1}>
                        <Grid item xs={12} sm={isAdminOrSuporte ? 3 : 4}>
                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Nome</InputLabel>
                            <MDInput size="small" placeholder="Nome..." value={filters.nome} onChange={({ target }) => setFilters({ ...filters, nome: target.value })} onKeyDown={(e) => { if (e.key === 'Enter') handleApplyFilter(); }} fullWidth />
                        </Grid>
                        <Grid item xs={12} sm={isAdminOrSuporte ? 2 : 4}>
                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Status</InputLabel>
                            <Autocomplete
                                options={[{ label: 'Todos', value: '' }, { label: 'Ativos', value: true }, { label: 'Inativos', value: false }]}
                                value={[{ label: 'Todos', value: '' }, { label: 'Ativos', value: true }, { label: 'Inativos', value: false }].find(opt => opt.value === filters.ativo) || { label: 'Todos', value: '' }}
                                onChange={(event, newValue) => {
                                    setFilters({ ...filters, ativo: newValue ? newValue.value : '' });
                                }}
                                isOptionEqualToValue={(option, value) => option.value === value?.value}
                                renderInput={(params) => <MDInput {...params} size="small" placeholder="Status..." />}
                                disableClearable
                            />
                        </Grid>
                        {isAdminOrSuporte && (
                            <Grid item xs={12} sm={3}>
                                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Categoria</InputLabel>
                                <Autocomplete
                                    options={categoriaOcorrenciaEnum}
                                    value={categoriaOcorrenciaEnum.find(opt => opt.code === filters.categoria) || null}
                                    onChange={(event, newValue) => {
                                        setFilters({ ...filters, categoria: newValue ? newValue.code : '' });
                                    }}
                                    getOptionLabel={opt => opt.label}
                                    isOptionEqualToValue={(option, value) => option.code === value?.code}
                                    renderInput={(params) => <MDInput {...params} size="small" placeholder="Categoria..." />}
                                />
                            </Grid>
                        )}
                        <Grid item xs={12} sm={4} display="flex" alignItems="center" justifyContent="flex-end" pt={1}>
                            <MDBox display="flex" gap={1} mt={3}>
                                <MDButton size="medium" variant="contained" color="info" onClick={() => { setSelectedId(null); setIsCreateEdit(true); }}>Adicionar</MDButton>
                                <MDButton size="medium" variant="contained" color="info" onClick={handleCleanFilter}>Limpar</MDButton>
                                <MDButton size="medium" variant="contained" color="info" onClick={handleApplyFilter}>Aplicar</MDButton>
                            </MDBox>
                        </Grid>
                    </Grid>
                </Card>
                <DataTable loading={loadingTable} columnData={userColumns} data={data} fetchData={fetchData} totalPages={pages} totalRecords={recordsTotal} changeFilter={aplicar} tableState={tableState} />
            </Grid>
        </Grid>
    );
}
